import { observable, action, makeObservable, computed, runInAction } from "mobx";
import { RootStore } from "./RootStore";
import { ASSEMBLY_STATE } from "./assemblies/AssemblyStore";
import { apolloClientInstance } from "src/network/apolloClientInstance";
import {
    GetAssemblyHistoryByAssemblyId,
    GetAssemblyHistoryByAssemblyId_assembly_agenda_notification_history,
    GetAssemblyHistoryByAssemblyId_assembly_date_notification_history,
    GetAssemblyHistoryByAssemblyId_assembly_protocol_notification_history,
    GetAssemblyHistoryByAssemblyId_assembly_state_history,
    GetAssemblyHistoryByAssemblyIdVariables
} from "src/api/generated/GetAssemblyHistoryByAssemblyId";
import { GET_ASSEMBLY_HISTORY_BY_ASSEMBLYID, SUBCRIPTION_GET_LAST_INSERTED_ASSEMBLY_EVENT } from "src/api/assembly";
import i18next from "i18next";
import { format, utcToZonedTime } from "date-fns-tz";
import {
    GetLastAssemblyEventById,
    GetLastAssemblyEventByIdVariables
} from "src/api/generated/GetLastAssemblyEventById";
import { MessageType } from "src/components/notifications/Notifier";
import { GET_AGENDAFILES_BY_ASSEMBLYID } from "src/api/agenda";
import {
    GetAgendaFilesByAssemblyId,
    GetAgendaFilesByAssemblyIdVariables
} from "src/api/generated/GetAgendaFilesByAssemblyId";
import { FILE_ENTITY, FILE_TYPE } from "src/enums";
import { IFile } from "src/types";
import { Role } from "src/network/User";

export enum COMMUNICATION_TIMELINE_ENTRY {
    DATE_PUBLISHED = "date_published",
    AGENDA_PUBLISHED = "agenda_published",
    PROTOCOL_PUBLISHED = "protocol_published",
    CLOSED = "closed"
}

export interface ICommunicationChangeEntry {
    date?: string;
    username?: string;
    label?: string;
}

export interface ICommunicationEntry {
    state?: COMMUNICATION_TIMELINE_ENTRY;
    isActive: boolean;
    statusChangeEntry?: ICommunicationChangeEntry;
    publishChangeEntry?: ICommunicationChangeEntry;
}

export class CommunicationStore {
    rootStore: RootStore;

    isCommunicationStoreInitializing: boolean = true;
    assemblyStateChangeHistory: GetAssemblyHistoryByAssemblyId_assembly_state_history[] = [];
    assemblyDateNotificationHistory: GetAssemblyHistoryByAssemblyId_assembly_date_notification_history[] = [];
    assemblyAgendaNotificationHistory: GetAssemblyHistoryByAssemblyId_assembly_agenda_notification_history[] = [];
    assemblyProtocolNotificationHistory: GetAssemblyHistoryByAssemblyId_assembly_protocol_notification_history[] = [];
    assemblyEventStateSubscription: any = undefined;
    isProtocolExists: boolean = false;
    isAgendaItemFileExists: boolean = false;
    isHasNoActiveCoonwerDisplayed: boolean = false;
    selectedProtocolPdfFile: File | undefined = undefined;
    isUploadingProtocolPdf: boolean = false;

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;

        makeObservable(this, {
            isCommunicationStoreInitializing: observable,
            assemblyStateChangeHistory: observable,
            assemblyDateNotificationHistory: observable,
            assemblyAgendaNotificationHistory: observable,
            assemblyProtocolNotificationHistory: observable,
            assemblyEventStateSubscription: observable,
            isProtocolExists: observable,
            isAgendaItemFileExists: observable,
            isHasNoActiveCoonwerDisplayed: observable,
            selectedProtocolPdfFile: observable,
            isUploadingProtocolPdf: observable,

            setAssemblyHistory: action,
            setAssemblyEventStateSubscription: action,
            setIsCommunicationStoreInitializing: action,
            setIsHasNoActiveCoonwerDisplayed: action,
            setSelectedProtocolPdfFile: action,
            uploadSelectedProtocolPdf: action,

            communicationEntries: computed
        });
    }

    /* Actions */
    setAssemblyHistory(assemblyHistory: GetAssemblyHistoryByAssemblyId) {
        this.assemblyStateChangeHistory = assemblyHistory?.assembly_state_history ?? [];
        this.assemblyDateNotificationHistory = assemblyHistory?.assembly_date_notification_history ?? [];
        this.assemblyAgendaNotificationHistory = assemblyHistory?.assembly_agenda_notification_history ?? [];
        this.assemblyProtocolNotificationHistory = assemblyHistory?.assembly_protocol_notification_history ?? [];
    }

    setAssemblyEventStateSubscription(assemblyEventStateSubscription: any) {
        this.assemblyEventStateSubscription = assemblyEventStateSubscription;
    }

    setIsCommunicationStoreInitializing(isInitializing: boolean) {
        this.isCommunicationStoreInitializing = isInitializing;
    }

    setIsHasNoActiveCoonwerDisplayed = (isHasNoActiveCoonwerDisplayed: boolean) => {
        this.isHasNoActiveCoonwerDisplayed = isHasNoActiveCoonwerDisplayed;
    };

    /* Computeds / Derived Values */
    get communicationEntries(): ICommunicationEntry[] {
        return Object.values(COMMUNICATION_TIMELINE_ENTRY).map((timelineEntryType) => {
            const entryTypeInteger = this.mapCommunicationStateToInteger(timelineEntryType);
            const assemblyStateInteger = this.rootStore.assemblyStore.mapStateToInteger(
                this.rootStore.assemblyStore.currentAssembly?.state || ASSEMBLY_STATE.PREPARING
            );
            const isEntryActive = this.getIsCommunicationEntryActive(assemblyStateInteger, entryTypeInteger);
            const entryStateHistory = this.assemblyStateChangeHistory.filter(
                (entry) => entry.new_val.state === entryTypeInteger
            );
            const entryNotificationHistory =
                timelineEntryType === COMMUNICATION_TIMELINE_ENTRY.DATE_PUBLISHED
                    ? this.assemblyDateNotificationHistory
                    : timelineEntryType === COMMUNICATION_TIMELINE_ENTRY.AGENDA_PUBLISHED
                      ? this.assemblyAgendaNotificationHistory
                      : timelineEntryType === COMMUNICATION_TIMELINE_ENTRY.PROTOCOL_PUBLISHED
                        ? this.assemblyProtocolNotificationHistory
                        : [];

            const statusChangeEntry: ICommunicationChangeEntry | undefined =
                isEntryActive && entryStateHistory.length > 0
                    ? {
                          label: i18next
                              .t(
                                  `screens.assemblies.communicationcontent.state_change_labels.${
                                      entryTypeInteger === 1 || entryTypeInteger === 5 || entryTypeInteger === 6
                                          ? "published"
                                          : "closed"
                                  }`
                              )
                              .toString(),
                          date: this.getDate(entryStateHistory[0]?.inserted),
                          username: entryStateHistory[0]?.username ?? undefined
                      }
                    : undefined;

            const notifyChangeEntry: ICommunicationChangeEntry | undefined =
                isEntryActive && entryNotificationHistory.length > 0
                    ? {
                          label: i18next
                              .t(`screens.assemblies.communicationcontent.notify_change_labels.${entryTypeInteger}`)
                              .toString(),
                          date: this.getDate(entryNotificationHistory[0]?.inserted),
                          username: entryNotificationHistory[0]?.username ?? undefined
                      }
                    : undefined;
            return {
                state: timelineEntryType,
                isActive: isEntryActive,
                statusChangeEntry: statusChangeEntry,
                publishChangeEntry: notifyChangeEntry
            } as ICommunicationEntry;
        });
    }

    /* Helpers */
    initAssemblyAndHistory = async (assemblyId: string) => {
        try {
            this.setIsCommunicationStoreInitializing(true);
            if (this.rootStore.assemblyStore.currentAssembly?.id !== assemblyId) {
                await this.rootStore.assemblyStore.loadCurrentAssembly(assemblyId);
            }
            await this.loadAssemblyHistory(assemblyId);
        } catch (error) {
            console.error("Error loading assembly data:", error);
        } finally {
            this.setIsCommunicationStoreInitializing(false);
        }
    };

    loadAssemblyHistory = async (assemblyId: string) => {
        try {
            const { data }: { data: GetAssemblyHistoryByAssemblyId } = await apolloClientInstance.query<
                GetAssemblyHistoryByAssemblyId,
                GetAssemblyHistoryByAssemblyIdVariables
            >({
                query: GET_ASSEMBLY_HISTORY_BY_ASSEMBLYID,
                fetchPolicy: "no-cache",
                variables: {
                    assemblyid: assemblyId
                }
            });

            this.setAssemblyHistory(data);
        } catch (error) {
            console.error("Error loading assembly history:", error);
        }
    };

    getIsCommunicationEntryActive(currentAssemblyState: number, state: number): boolean {
        let isActive = false;

        switch (currentAssemblyState) {
            case 1: // Agenda Published
                isActive = state === 1 || state === 5;
                break;
            case 5: // Date Published
                isActive = state === 5;
                break;
            case 6: // Protocol Published - Make all three active
                isActive = state === 1 || state === 5 || state === 6;
                break;
            case 9: // Some other state where all should be active
                isActive = state <= 9;
                break;
            default:
        }

        return isActive;
    }

    mapCommunicationStateToInteger(state: COMMUNICATION_TIMELINE_ENTRY): number {
        switch (state) {
            case COMMUNICATION_TIMELINE_ENTRY.AGENDA_PUBLISHED:
                return 1;
            case COMMUNICATION_TIMELINE_ENTRY.DATE_PUBLISHED:
                return 5;
            case COMMUNICATION_TIMELINE_ENTRY.PROTOCOL_PUBLISHED:
                return 6;
            case COMMUNICATION_TIMELINE_ENTRY.CLOSED:
                return 9;
            default:
                throw new Error(`Unknown state: ${state}`);
        }
    }

    getDate = (timestamp: string) => {
        if (timestamp) {
            const timeZone = "Europe/Zurich";
            const zonedDate = utcToZonedTime(timestamp + "Z", timeZone);
            return format(zonedDate, "dd.MM.yyyy HH:mm", { timeZone });
        }
        return undefined;
    };

    stopAssemblyEventStateSubscription() {
        if (this.assemblyEventStateSubscription) {
            this.assemblyEventStateSubscription.unsubscribe();
            this.setAssemblyEventStateSubscription(undefined);

            this.rootStore.assemblyStore.setIsAssemblyInsertedEventLoaded(true);

            this.rootStore.uiStore.printStatusMessage(
                i18next.t("screens.realestate.assembly.save_notification_success"),
                MessageType.SUCCESS
            );
        }
    }

    startAssemblyEventStateSubscription(assemblyState: ASSEMBLY_STATE) {
        if (this.assemblyEventStateSubscription) {
            return;
        }
        const timeZone = "Europe/Zurich";
        const zonedDate = utcToZonedTime(new Date(Date.now()), timeZone);
        const startDate = format(zonedDate, "yyyy-MM-dd HH:mm:ss.SSS", { timeZone });

        const assemblyId = this.rootStore.assemblyStore.currentAssembly?.id;
        if (assemblyId && assemblyId !== "") {
            this.setAssemblyEventStateSubscription(
                apolloClientInstance
                    .subscribe<GetLastAssemblyEventById, GetLastAssemblyEventByIdVariables>({
                        query: SUBCRIPTION_GET_LAST_INSERTED_ASSEMBLY_EVENT,
                        variables: {
                            assemblyid: assemblyId,
                            lastinserted: startDate,
                            notificationtype: this.rootStore.assemblyStore.mapStateToInteger(assemblyState)
                        }
                    })
                    .subscribe({
                        next: ({ data }) => {
                            if (data?.noti_assemblyevents && data.noti_assemblyevents.length > 0) {
                                this.onIsAssemblyAssignedToRealestateSubscriptionDataReceived(assemblyId);
                            }
                        },
                        error: () => {
                            // Error is logged by Apollo errorLink
                            this.stopAssemblyEventStateSubscription();
                        }
                    })
            );
        }
    }

    onIsAssemblyAssignedToRealestateSubscriptionDataReceived = (assemblyId: string) => {
        this.stopAssemblyEventStateSubscription();
        this.loadAssemblyHistory(assemblyId);
    };

    async getProtocolOrAgendaitemFile(assemblyId: string) {
        try {
            const { data }: { data: GetAgendaFilesByAssemblyId } = await apolloClientInstance.query<
                GetAgendaFilesByAssemblyId,
                GetAgendaFilesByAssemblyIdVariables
            >({
                query: GET_AGENDAFILES_BY_ASSEMBLYID,
                fetchPolicy: "no-cache",
                variables: { assemblyid: assemblyId }
            });
            runInAction(() => {
                if (data.assemblyfiles) {
                    if (data.assemblyfiles?.some((file) => file.type === 313)) {
                        this.isProtocolExists = true;
                    } else {
                        this.isProtocolExists = false;
                    }

                    if (data.assemblyfiles?.some((file) => file.type === 314)) {
                        this.isAgendaItemFileExists = true;
                    } else {
                        this.isAgendaItemFileExists = false;
                    }
                }
            });
        } catch (error) {
            console.error("Failed to load data from view file_v_generate_new_protocol:", error);
            throw error;
        }
    }

    setSelectedProtocolPdfFile = (file: File | undefined) => {
        this.selectedProtocolPdfFile = file;
    };

    private fileToBase64DataOnly = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onerror = () => reject(reader.error);
            reader.onload = () => {
                const result = String(reader.result ?? "");
                // result looks like: "data:application/pdf;base64,AAAA..."
                const commaIndex = result.indexOf(",");
                resolve(commaIndex >= 0 ? result.slice(commaIndex + 1) : result);
            };
            reader.readAsDataURL(file);
        });
    };

    private buildIFileFromBrowserFile = async (file: File, type: number): Promise<IFile> => {
        const base64String = await this.fileToBase64DataOnly(file);

        const extension = (file.name.split(".").pop() ?? "pdf").toLowerCase();

        const filename = file.name.endsWith(`.${extension}`) ? file.name.replace(`.${extension}`, "") : file.name;

        return {
            mimetype: file.type || "application/pdf",
            extension,
            name: filename,
            fileDate: new Date().toISOString(),
            base64String,
            type
        } as IFile;
    };

    uploadSelectedProtocolPdf = async (assemblyId: string) => {
        if (!this.selectedProtocolPdfFile) return;

        const file = this.selectedProtocolPdfFile;

        // Basic validation
        if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
            this.rootStore.uiStore.printStatusMessage("Please select a PDF document.", MessageType.ERROR);
            return;
        }

        this.isUploadingProtocolPdf = true;

        try {
            const metadataFile = await this.buildIFileFromBrowserFile(file, FILE_TYPE.ASSEMBLY_PROTOCOL);
            metadataFile.fileBlob = file;

            const newFileId = await this.rootStore.fileStore.uploadFilesBulkWithRoles(
                metadataFile,
                assemblyId,
                FILE_ENTITY.COND_ASSEMBLY,
                [Role.MANAGER, Role.COOWNER]
            );

            runInAction(() => {
                this.isProtocolExists = !!newFileId;
                this.selectedProtocolPdfFile = undefined;
            });

            await this.getProtocolOrAgendaitemFile(assemblyId);
            await this.rootStore.protocolStore.getAssemblyProtocolFile(assemblyId);
            this.rootStore.protocolStore.setProtocolFileId(assemblyId);
        } catch (e) {
            console.error(e);
            this.rootStore.uiStore.printStatusMessage("Upload failed.", MessageType.ERROR);
        } finally {
            runInAction(() => {
                this.isUploadingProtocolPdf = false;
            });
        }
    };
}
